(() => {
  const ROOT = '[data-vh-pdp-delivery]';
  const BUSINESS_DAY_MIN = 1;
  const BUSINESS_DAY_MAX = 2;
  const PREORDER_WEEK_MIN = 6;
  const PREORDER_WEEK_MAX = 8;

  const plMonths = [
    'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
    'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
  ];

  const clampQty = (value) => {
    const qty = Math.floor(Number(value));
    return Number.isFinite(qty) && qty > 0 ? Math.min(qty, 99) : 1;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const escapeSelector = (value) => (window.CSS && typeof window.CSS.escape === 'function')
    ? window.CSS.escape(String(value || ''))
    : String(value || '').replace(/["\\]/g, '\\$&');

  const today = () => new Date();
  const formatDayMonth = (date) => `${date.getDate()} ${plMonths[date.getMonth()]}`;
  const todayLabel = () => `Dziś, ${formatDayMonth(today())}`;

  const addBusinessDays = (start, count) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let left = Math.max(0, Math.floor(count));
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) left -= 1;
    }
    return date;
  };

  const addWeeks = (start, weeks) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    date.setDate(date.getDate() + Math.round(weeks * 7));
    if (date.getDay() === 6) date.setDate(date.getDate() + 2);
    if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    return date;
  };

  const formatRange = (from, to) => {
    if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
      return `${from.getDate()}–${to.getDate()} ${plMonths[to.getMonth()]}`;
    }
    return `${formatDayMonth(from)} – ${formatDayMonth(to)}`;
  };

  const instockRange = () => [
    addBusinessDays(today(), BUSINESS_DAY_MIN),
    addBusinessDays(today(), BUSINESS_DAY_MAX),
  ];

  const preorderRange = () => [
    addBusinessDays(addWeeks(today(), PREORDER_WEEK_MIN), 1),
    addBusinessDays(addWeeks(today(), PREORDER_WEEK_MAX), 1),
  ];

  const parseVariants = (root) => {
    const node = root.querySelector('[data-vh-delivery-variants]');
    if (!node?.textContent) return [];
    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('[Valleans delivery] Invalid variant data', error);
      return [];
    }
  };

  const getQuantity = (root) => {
    const productId = root.dataset.productId || '';
    const form = document.querySelector(`product-form-component[data-product-id="${escapeSelector(productId)}"]`)
      || document.querySelector('product-form-component');
    const input = form?.querySelector('input[name="quantity"], [ref="quantityInput"]')
      || document.querySelector('quantity-selector-component input[name="quantity"]');
    return clampQty(input?.value || 1);
  };

  const formVariantId = (root, variants) => {
    const valid = new Set(variants.map((variant) => String(variant.id)));
    const productId = root.dataset.productId || '';
    const form = document.querySelector(`product-form-component[data-product-id="${escapeSelector(productId)}"]`)
      || document.querySelector('product-form-component');
    const input = form?.querySelector('input[name="id"]');
    if (input?.value && valid.has(String(input.value))) return Number(input.value);
    return null;
  };

  const stateFor = (root) => {
    const data = root._vhDelivery;
    const variants = data?.variants || [];
    const variantId = Number(data?.variantId || formVariantId(root, variants) || root.dataset.selectedVariantId || 0);
    const variant = variants.find((item) => Number(item.id) === variantId)
      || variants.find((item) => item.available)
      || variants[0]
      || null;
    const requestedQty = getQuantity(root);
    const stockQty = Math.max(0, Math.floor(Number(variant?.inventory_quantity || 0)));
    const canContinue = String(variant?.inventory_policy || '').toLowerCase() === 'continue';

    if (stockQty >= requestedQty && stockQty > 0) return { mode: 'instock', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
    if (stockQty > 0 && requestedQty > stockQty && canContinue) return { mode: 'mixed', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: requestedQty - stockQty };
    if (stockQty <= 0 && canContinue) return { mode: 'preorder', variantId: Number(variant?.id || 0), requestedQty, stockQty: 0, missingQty: requestedQty };
    if (stockQty > 0 && requestedQty > stockQty) return { mode: 'limited', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
    return { mode: 'unavailable', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
  };

  const timelineMarkup = (steps) => steps.map((step, index) => `
    <div class="vh-pdp-delivery__step">
      <span class="vh-pdp-delivery__node${index === 0 ? ' is-current' : ''}${index === steps.length - 1 ? ' is-final' : ''}"></span>
      <span class="vh-pdp-delivery__step-label">
        <span class="vh-pdp-delivery__step-title">${escapeHtml(step.title)}</span>
        ${step.sub ? `<span class="vh-pdp-delivery__step-sub">${escapeHtml(step.sub)}</span>` : ''}
      </span>
    </div>`).join('');

  const orderSteps = () => [
    { title: 'Zamówienie', sub: todayLabel() },
    { title: 'Produkcja', sub: '6–8 tygodni' },
    { title: 'Kontrola', sub: 'jakości' },
    { title: 'Dostawa', sub: '1 dzień · DPD' },
  ];

  const readySteps = () => {
    const [from, to] = instockRange();
    return [
      { title: 'Zamówienie', sub: todayLabel() },
      { title: 'Pakowanie', sub: 'od razu' },
      { title: 'Dostawa DPD', sub: '1 dzień' },
      { title: 'Otrzymanie', sub: formatRange(from, to) },
    ];
  };

  const mixedSteps = () => [
    { title: 'Zamówienie', sub: todayLabel() },
    { title: 'Produkcja', sub: 'brakujące · 6–8 tyg.' },
    { title: 'Kontrola', sub: 'jakości' },
    { title: 'Dostawa', sub: 'DPD' },
  ];

  const setStatus = (root, cls, text) => {
    const status = root.querySelector('[data-vh-delivery-status]');
    status.className = `vh-pdp-delivery__status ${cls}`;
    status.textContent = text;
  };

  const setEta = (root, label, value) => {
    const eta = root.querySelector('[data-vh-delivery-eta]');
    eta.innerHTML = `${escapeHtml(label)}${value ? ` <strong>${escapeHtml(value)}</strong>` : ''}`;
  };

  const setTimeline = (root, steps) => {
    root.querySelector('[data-vh-delivery-timeline]').innerHTML = timelineMarkup(steps);
  };

  const setDetails = (root, text) => {
    root.querySelector('[data-vh-delivery-details]').textContent = text;
  };

  const render = (root) => {
    const state = stateFor(root);
    const data = root._vhDelivery;
    const key = `${state.variantId}:${state.requestedQty}:${state.stockQty}:${state.mode}:${formatDayMonth(today())}`;
    if (data?.lastKey === key) return;
    data.lastKey = key;
    root.dataset.mode = state.mode;

    if (state.mode === 'instock') {
      const eta = formatRange(...instockRange());
      setStatus(root, 'is-ready', 'Dostępna od ręki');
      setEta(root, 'Przewidywana dostawa', eta);
      setTimeline(root, readySteps());
      setDetails(root, 'Produkt jest dostępny od ręki. Po złożeniu zamówienia od razu przygotowujemy i pakujemy go do wysyłki, następnie przekazujemy do DPD. Otrzymasz zamówienie w ciągu 1–2 dni roboczych.');
      return;
    }

    if (state.mode === 'preorder') {
      const eta = formatRange(...preorderRange());
      setStatus(root, 'is-order', 'Na zamówienie');
      setEta(root, 'Przewidywana dostawa', eta);
      setTimeline(root, orderSteps());
      setDetails(root, 'Po złożeniu zamówienia produkt trafia do produkcji, która trwa 6–8 tygodni. Następnie przechodzi kontrolę jakości, pakowanie i zabezpieczenie do transportu, po czym przekazujemy go do DPD. Dostawa kurierska zajmuje zwykle 1 dzień roboczy.');
      return;
    }

    if (state.mode === 'mixed') {
      const fastEta = formatRange(...instockRange());
      const orderEta = formatRange(...preorderRange());
      setStatus(root, 'is-mixed', 'Częściowo dostępne');
      setEta(root, 'Dostawa w 2 terminach', `${fastEta} / ${orderEta}`);
      setTimeline(root, mixedSteps());
      setDetails(root, `Od ręki: ${state.stockQty} szt. — otrzymasz je w ciągu 1–2 dni roboczych. Na zamówienie: ${state.missingQty} szt. — produkcja trwa 6–8 tygodni, następnie kontrola jakości i dostawa DPD.`);
      return;
    }

    if (state.mode === 'limited') {
      const eta = formatRange(...instockRange());
      setStatus(root, 'is-limited', 'Ostatnie sztuki');
      setEta(root, `Dostępne: ${state.stockQty} szt. · Dostawa`, eta);
      setTimeline(root, readySteps());
      setDetails(root, `Dostępne od ręki: ${state.stockQty} szt. Po zamówieniu produkt od razu pakujemy i przekazujemy do DPD. Otrzymasz go w ciągu 1–2 dni roboczych. Wybrana ilość (${state.requestedQty} szt.) przekracza aktualny stan magazynowy.`);
      return;
    }

    setStatus(root, 'is-unavailable', 'Tymczasowo niedostępne');
    setEta(root, 'Termin dostawy', 'brak dostępnego terminu');
    setTimeline(root, []);
    setDetails(root, 'Ten wariant nie jest obecnie dostępny do zamówienia.');
  };

  const updateVariant = (root, id) => {
    const data = root._vhDelivery;
    const valid = data?.variants?.some((variant) => Number(variant.id) === Number(id));
    if (!valid) return false;
    data.variantId = Number(id);
    root.dataset.selectedVariantId = String(id);
    data.lastKey = '';
    render(root);
    return true;
  };

  const syncFromForm = (root) => {
    const id = formVariantId(root, root._vhDelivery?.variants || []);
    if (id) updateVariant(root, id);
    else {
      root._vhDelivery.lastKey = '';
      render(root);
    }
  };

  const initRoot = (root) => {
    if (root._vhDelivery) return;
    const variants = parseVariants(root);
    if (!variants.length) return;
    root._vhDelivery = {
      variants,
      variantId: Number(root.dataset.selectedVariantId || 0) || Number(variants[0].id),
      lastKey: '',
    };
    render(root);
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.(ROOT).forEach(initRoot);
  };

  document.addEventListener('variant:update', (event) => {
    const variantId = Number(event.detail?.resource?.id || 0);
    const productId = String(event.detail?.data?.productId || '');
    document.querySelectorAll(ROOT).forEach((root) => {
      if (productId && root.dataset.productId && productId !== String(root.dataset.productId)) return;
      if (variantId) updateVariant(root, variantId);
      else syncFromForm(root);
    });
  });

  document.addEventListener('quantity-selector:update', () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      root._vhDelivery.lastKey = '';
      render(root);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();
